import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, ArrowRight, Loader2 } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot
} from "@/components/ui/input-otp";
import Navbar from "@/components/Navbar";

const emailSchema = z.object({
  email: z.string().email("Please enter a valid email address")
});

const otpSchema = z.object({
  otp: z.string().length(6, "Please enter all 6 digits")
});

type EmailFormValues = z.infer<typeof emailSchema>;
type OtpFormValues = z.infer<typeof otpSchema>;

const AuthPage = () => {
  const navigate = useNavigate();
  const { sendEmailCode, verifyEmailCode } = useAuth();
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [showOtpForm, setShowOtpForm] = useState(false);

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: ""
    }
  });

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: ""
    }
  });

  useEffect(() => {
    const storedEmail = sessionStorage.getItem('auth_email');
    if (storedEmail) {
      emailForm.setValue('email', storedEmail);
      setEmail(storedEmail);
      setShowOtpForm(true);
      sessionStorage.removeItem('auth_email');
    }
  }, []);

  const onEmailSubmit = async (values: EmailFormValues) => {
    try {
      setIsSubmittingEmail(true);
      await sendEmailCode(values.email);
      setEmail(values.email);
      setShowOtpForm(true);
      toast.success("Verification code sent", {
        description: "Please check your email for the verification code"
      });
    } catch (error) {
      toast.error("Failed to send code", {
        description: "There was a problem sending the verification code"
      });
      console.error(error);
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  const onOtpSubmit = async (values: OtpFormValues) => {
    try {
      setIsVerifyingCode(true);
      const isSuccess = await verifyEmailCode(email, values.otp);
      
      if (isSuccess) {
        toast.success("Verification successful", {
          description: "You're now signed in to Travel Buddy"
        });
        navigate("/start-journey");
      } else {
        toast.error("Invalid verification code", {
          description: "Please check the code and try again"
        });
      }
    } catch (error) {
      toast.error("Verification failed", {
        description: "There was a problem verifying your code"
      });
      console.error(error);
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleBackToEmail = () => {
    setShowOtpForm(false);
  };

  return (
    <>
      <Navbar />
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] py-12 px-4">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold">Welcome to Visa Now</h1>
            <p className="text-gray-600 mt-2">
              {showOtpForm
                ? "Enter the verification code sent to your email"
                : "Sign in or create your account to get started"}
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
            {!showOtpForm ? (
              <Form {...emailForm}>
                <form
                  onSubmit={emailForm.handleSubmit(onEmailSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={emailForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email address</FormLabel>
                        <FormControl>
                          <div className="flex items-center border rounded-md focus-within:ring-2 focus-within:ring-black/10 focus-within:border-black/20 transition-all">
                            <div className="pl-3 text-gray-500">
                              <Mail size={18} />
                            </div>
                            <Input
                              placeholder="name@example.com"
                              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={isSubmittingEmail}
                  >
                    {isSubmittingEmail ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending code...
                      </>
                    ) : (
                      <>
                        Continue with Email
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            ) : (
              <Form {...otpForm}>
                <form
                  onSubmit={otpForm.handleSubmit(onOtpSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={otpForm.control}
                    name="otp"
                    render={({ field }) => (
                      <FormItem className="space-y-4">
                        <FormLabel>Verification code</FormLabel>
                        <FormControl>
                          <div className="flex justify-center">
                            <InputOTP
                              maxLength={6}
                              {...field}
                              render={({ slots }) => (
                                <InputOTPGroup>
                                  {slots.map((slot, index) => (
                                    <InputOTPSlot
                                      key={index}
                                      {...slot}
                                      className="w-10 h-12 text-xl"
                                      index={index}
                                    />
                                  ))}
                                </InputOTPGroup>
                              )}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-3">
                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={isVerifyingCode}
                    >
                      {isVerifyingCode ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        "Verify & Sign In"
                      )}
                    </Button>

                    <div className="flex justify-between text-sm">
                      <button
                        type="button"
                        onClick={handleBackToEmail}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        Use a different email
                      </button>
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800"
                        onClick={() => {
                          sendEmailCode(email);
                          toast.success("New code sent", {
                            description: "Please check your email for the new verification code"
                          });
                        }}
                      >
                        Resend code
                      </button>
                    </div>
                  </div>
                </form>
              </Form>
            )}

            <div className="mt-6 pt-6 border-t text-center text-sm text-gray-500">
              By continuing, you agree to Visa Now's
              <br />
              <a href="#" className="text-blue-600 hover:text-blue-800">
                Terms of Service
              </a>{" "}
              &{" "}
              <a href="#" className="text-blue-600 hover:text-blue-800">
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthPage;
